import React, { useEffect, useState } from "react";
import userAPI from "../../api/UserApi";
import { showError, showSuccess } from "../../utils/toastUtils";
import useAlphabeticInput from "../../hooks/useAlphabeticInput";
import { useNavigate } from "react-router-dom";
import { setAccessToken } from "../../api/axiosInstance";
import { handleApiValidationError } from "../../utils/apiErrorHandler";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Mail, Lock, User, ArrowRight } from "lucide-react";

function RegisterCanvas() {
  useAlphabeticInput();

  const INITIAL_FORM_DATA = {
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    confirmPassword: "",
  };

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValidEmail = emailRegex.test(formData.email);
  const isValidPassword = formData.password.length >= 6;

  const validate = () => {
    const newErrors = {};

    if (!formData.firstName) {
      newErrors.firstName = "Please Enter First Name";
    }

    if (!formData.lastName) {
      newErrors.lastName = "Please Enter Last Name";
    }

    if (!formData.email) {
      newErrors.email = "Please Enter Email Address";
    } else if (!isValidEmail) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.password) {
      newErrors.password = "Enter Password";
    } else if (!isValidPassword) {
      newErrors.password = "Password must be at least 6 characters";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Enter Confirm Password";
    } else if (!isValidPassword) {
      newErrors.confirmPassword =
        "Confirm Password must be at least 6 characters";
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Password & Confirm Password must be same";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      const res = await userAPI.register(formData);
      if (res.code === 200) {
        showSuccess(res.message);
        setAccessToken(res.data.access_token);

        const user = {
          email: res.data.email,
          username: res.data.username,
        };

        localStorage.setItem("token", JSON.stringify(user));
        localStorage.setItem("accessToken", res.data.accessToken);

        navigate("/users/dashboard");
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    }
  };

  return (
    <div className="custom-register-wrapper">
      <Container fluid className="p-0">
        <Row className="g-0 custom-register-row">
          {/* Left Side: Editorial Visual */}
          <Col md={6} lg={7} className="d-none d-md-block">
            <div className="custom-register-visual">
              <div className="custom-register-visual-content">
                <span className="visual-tag">Ivoréa</span>
                <h3 className="visual-heading">
                  Join the <span>Collection</span>
                </h3>
                <p className="visual-desc">
                  Create an account to unlock personalized recommendations,
                  faster checkout, and early access to new arrivals.
                </p>
              </div>
            </div>
          </Col>

          {/* Right Side: Register Form */}
          <Col
            xs={12}
            md={6}
            lg={5}
            className="d-flex justify-content-center align-items-center"
          >
            <div className="custom-register-container">
              <div className="custom-register-header mb-4">
                <h2 className="custom-register-heading">Create Account</h2>
                <p className="custom-register-subheading">
                  Experience luxury gifting at your fingertips
                </p>
              </div>

              <Form
                method="post"
                className="custom-register-form"
                onSubmit={handleSubmit}
              >
                <div className="d-flex gap-3 register-flex">
                  <Form.Group
                    className="custom-register-field mb-3"
                    controlId="firstName"
                  >
                    <Form.Label className="custom-register-label">
                      First Name <span className="required-star">*</span>
                    </Form.Label>
                    <div className="custom-register-input-box">
                      <User size={18} className="custom-register-icon" />
                      <Form.Control
                        type="text"
                        name="firstName"
                        className="custom-register-input"
                        placeholder="First Name"
                        value={formData.firstName}
                        onChange={handleChange}
                      />
                    </div>
                    {errors?.firstName && (
                      <span className="custom-input-error">
                        {errors.firstName}
                      </span>
                    )}
                  </Form.Group>

                  <Form.Group
                    className="custom-register-field mb-3"
                    controlId="lastName"
                  >
                    <Form.Label className="custom-register-label">
                      Last Name <span className="required-star">*</span>
                    </Form.Label>
                    <div className="custom-register-input-box">
                      <User size={18} className="custom-register-icon" />
                      <Form.Control
                        type="text"
                        className="custom-register-input"
                        name="lastName"
                        placeholder="Last Name"
                        value={formData.lastName}
                        onChange={handleChange}
                      />
                    </div>
                    {errors?.lastName && (
                      <span className="custom-input-error">
                        {errors.lastName}
                      </span>
                    )}
                  </Form.Group>
                </div>

                <Form.Group
                  className="custom-register-field mb-3"
                  controlId="email"
                >
                  <Form.Label className="custom-register-label">
                    Email Address <span className="required-star">*</span>
                  </Form.Label>
                  <div className="custom-register-input-box">
                    <Mail size={18} className="custom-register-icon" />
                    <Form.Control
                      type="email"
                      className="custom-register-input"
                      name="email"
                      placeholder="Email Address"
                      value={formData.email}
                      onChange={handleChange}
                    />
                  </div>
                  {errors?.email && (
                    <span className="custom-input-error">
                      {errors.email}
                    </span>
                  )}
                </Form.Group>

                <div className="d-flex gap-3 register-flex">
                  <Form.Group
                    className="mb-3 custom-register-field"
                    controlId="password"
                  >
                    <Form.Label className="custom-register-label">
                      Password <span className="required-star">*</span>
                    </Form.Label>
                    <div className="custom-register-input-box">
                      <Lock size={18} className="custom-register-icon" />
                      <Form.Control
                        type="password"
                        className="custom-register-input"
                        name="password"
                        placeholder="Password"
                        value={formData.password}
                        onChange={handleChange}
                      />
                    </div>
                    {errors?.password && (
                      <span className="custom-input-error">
                        {errors.password}
                      </span>
                    )}
                  </Form.Group>

                  <Form.Group
                    className="mb-3 custom-register-field"
                    controlId="confirmPassword"
                  >
                    <Form.Label className="custom-register-label">
                      Confirm Password <span className="required-star">*</span>
                    </Form.Label>
                    <div className="custom-register-input-box">
                      <Lock size={18} className="custom-register-icon" />
                      <Form.Control
                        type="password"
                        className="custom-register-input"
                        name="confirmPassword"
                        placeholder="Confirm Password"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                      />
                    </div>
                    {errors?.confirmPassword && (
                      <span className="custom-input-error">
                        {errors.confirmPassword}
                      </span>
                    )}
                  </Form.Group>
                </div>

                <Button
                  className="custom-register-submit w-100"
                  type="submit"
                >
                  Register <ArrowRight size={18} className="ms-2" />
                </Button>

                <div className="custom-register-footer mt-4 text-center">
                  <span className="footer-muted-text">
                    Already have an account?
                  </span>
                  <Link
                    to="/users/login"
                    className="custom-register-link-main ms-2"
                  >
                    Sign In
                  </Link>
                </div>
              </Form>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default RegisterCanvas;